<template>
  <div class="settings-services-container m-4">
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">Services</h5>
      <div class="ml-auto mr-3" style="width: 300px">
        <b-input v-model="search" placeholder="Search..." />
      </div>
      <b-button
        class="pt-2 pb-2"
        size="sm"
        @click="addService"
        variant="primary"
      >
        New
      </b-button>
    </div>
    <grid
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="items"
      :columnLines="false"
      :searchFeature="true"
      :stripeFeature="false"
    />
    <b-modal
      ref="form-modal"
      :title="serviceId ? 'Edit Service' : 'Add Service'"
      @ok.prevent="submitService"
      header-bg-variant="primary"
      header-text-variant="white"
      header-class="bg-purple"
      no-close-on-backdrop
      centered
    >
      <template v-slot:modal-footer="{ ok, cancel }">
        <b-button variant="secondary" @click="cancel()"> Close</b-button>
        <b-button variant="primary" @click="ok()"> Save</b-button>
      </template>

      <div class="modal-body">
        <b-row>
          <b-col cols="10">
            <b-form-group
              label="Name"
              :state="formErrors.name.length == 0 ? null : false"
              :invalid-feedback="formErrors.name.join(', ')"
            >
              <b-form-input
                v-model="form.name"
                :state="formErrors.name.length == 0 ? null : false"
                maxLength="500"
              />
            </b-form-group>
            <div class="d-flex justify-content-start align-items-center">
              <b-form-group style="margin-bottom: 0">
                <span style="font-size: 14px;">Schedule Color</span>
                <service-color-picker
                  v-model="form.color"
                  @removeColor="removeColor()"
                ></service-color-picker>
                <span
                  class="mt-2"
                  style="font-size: 10px; color: dodgerblue; line-height: 30%"
                >
                  This will override the default colors in the schedule.
                </span>
              </b-form-group>
            </div>
          </b-col>
        </b-row>

        <b-button
          size="lg"
          v-b-toggle.collapse-1
          class="my-3 font-weight-bold col-12 d-flex justify-content-between"
          variant="light"
          >Availability and duration<span class="when-opened"
            ><i class="fa fa-chevron-up" aria-hidden="true"></i></span
          ><span class="when-closed"
            ><i class="fa fa-chevron-down" aria-hidden="true"></i></span
        ></b-button>
        <b-collapse id="collapse-1" visible>
          <b-container>
            <b-row class="mr-2 justify-content-left">
              <b-col class="col-4">
                <b-form-group
                  label="Default Duration"
                  :state="
                    formErrors.defaultDurationMinutes.value < 0 ? true : false
                  "
                  :invalid-feedback="
                    formErrors.defaultDurationMinutes.join('. ')
                  "
                >
                  <b-form-input
                    class="form-control"
                    v-model="form.defaultDurationMinutes"
                    type="number"
                    min="1"
                    trim
                  />
                </b-form-group>
              </b-col>
              <b-row class="col-8 ma-0">
                <b-col class="pl-0 pr-1" md="auto">
                  <h5 class="custom-label">Minutes</h5>
                </b-col>
                <b-col class="pl-0 pr-0 mt-1" md="auto">
                  <p class="custom-label font-italic" style="font-size: 12px;">
                    (Equivalent to 1 service unit)
                  </p>
                </b-col>
              </b-row>
            </b-row>
            <b-row>
              <b-col>
                <b-form-group>
                  <b-form-checkbox
                    style="font-size: 14px"
                    v-model="form.appointmentRequestEnable"
                  >
                    Available for existing patients
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>

            <b-row class="pt-0">
              <b-col>
                <b-form-group>
                  <b-form-checkbox
                    style="font-size: 14px"
                    v-model="form.appointmentRequestNewPatientEnable"
                    class="mr-2"
                  >
                    Available for new patients
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>

            <b-form-group
              style="font-size: 14px"
              class="inline"
              :state="formErrors.blockOffMinutes.value < 0 ? true : false"
              :invalid-feedback="formErrors.blockOffMinutes.join('. ')"
            >
              <b-row class="items-center ml-1">
                <p class="form-text sm-1">Block Off</p>
                <b-form-input
                  style="margin: 0px; width: 60px"
                  size="sm"
                  class="form-group mx-2"
                  v-model="form.blockOffMinutesBefore"
                  type="number"
                  min="1"
                  trim
                />
                <p class="form-text sm-1">minutes before and</p>
                <b-form-input
                  style="margin: 0px; width: 60px"
                  size="sm"
                  class="form-group mx-2"
                  v-model="form.blockOffMinutesAfter"
                  type="number"
                  min="1"
                  trim
                />
                <p class="form-text sm-1">minutes after appointment.</p>
              </b-row>
            </b-form-group>
          </b-container>
        </b-collapse>

        <b-button
          size="lg"
          v-b-toggle.collapse-documents
          class="my-3 font-weight-bold col-12 d-flex justify-content-between"
          variant="light"
          >Documents<span class="when-opened"
            ><i class="fa fa-chevron-up" aria-hidden="true"></i></span
          ><span class="when-closed"
            ><i class="fa fa-chevron-down" aria-hidden="true"></i></span
        ></b-button>
        <b-collapse id="collapse-documents" visible>
          <b-container>
            <b-form-group label="Related documents">
              <multiple-select :options="formOptions" v-model="form.formIds" />
            </b-form-group>
          </b-container>
        </b-collapse>

        <b-button
          size="lg"
          v-b-toggle.collapse-billing
          class="my-3 font-weight-bold col-12 d-flex justify-content-between"
          variant="light"
          >Billing and codes<span class="when-opened"
            ><i class="fa fa-chevron-up" aria-hidden="true"></i></span
          ><span class="when-closed"
            ><i class="fa fa-chevron-down" aria-hidden="true"></i></span
        ></b-button>
        <b-collapse id="collapse-billing" visible>
          <b-container>
            <b-row>
              <b-col>
                <b-form-group>
                  <b-form-checkbox v-model="form.billable">
                    Billable Service
                  </b-form-checkbox>
                </b-form-group>
              </b-col>
            </b-row>
            <b-form-group
              label="Billing Code"
              :state="formErrors.billingCode.length == 0 ? null : false"
              :invalid-feedback="formErrors.billingCode.join('. ')"
            >
              <b-form-input
                :disabled="!form.billable"
                v-model="form.billingCode"
                :state="formErrors.billingCode.length == 0 ? null : false"
                trim
                maxlength="50"
                v-on:keypress="isLetterOrNumber"
              />
            </b-form-group>
            <b-form-group
              :state="formErrors.modifiers.length == 0 ? null : false"
              :invalid-feedback="formErrors.modifiers.join('. ')"
            >
              <b-row>
                <b-col class="pr-0">
                  <b-form-group label="Modifier 1">
                    <b-form-input
                      :disabled="!form.billable"
                      v-model="form.mod1"
                      maxLength="20"
                    />
                  </b-form-group>
                </b-col>
                <b-col class="pr-0">
                  <b-form-group label="Modifier 2">
                    <b-form-input
                      :disabled="!form.billable"
                      v-model="form.mod2"
                      maxLength="20"
                    />
                  </b-form-group>
                </b-col>
                <b-col class="pr-0">
                  <b-form-group label="Modifier 3">
                    <b-form-input
                      :disabled="!form.billable"
                      v-model="form.mod3"
                      maxLength="20"
                    />
                  </b-form-group>
                </b-col>
                <b-col>
                  <b-form-group label="Modifier 4">
                    <b-form-input
                      :disabled="!form.billable"
                      v-model="form.mod4"
                      maxLength="20"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </b-form-group>
            <b-form-group
              label="Default Cost Per Unit ($)"
              :state="formErrors.costPerUnit.value < 0 ? true : false"
              :invalid-feedback="formErrors.costPerUnit.join('. ')"
            >
              <b-row>
                <b-col class="pr-2" cols="6">
                  <b-input-group prepend="$">
                    <b-form-input
                      :disabled="!form.billable"
                      class="form-control"
                      v-model="form.costPerUnit"
                      type="number"
                      min="0"
                      trim
                    />
                  </b-input-group>
                </b-col>
              </b-row>
            </b-form-group>

            <b-form-group>
              <b-form-checkbox
                :disabled="!form.billable"
                v-model="form.fullyPaidByPatient"
              >
                Fully Paid by Patient
              </b-form-checkbox>
            </b-form-group>

            <service-billing-code-cost-variation-condition-editor
              v-model="form.costVariations"
              :defaultCost="form.costPerUnit"
              ref="conditionEditor"
              :service="form"
            />
          </b-container>
        </b-collapse>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid.vue";
import ServiceColorPicker from "../controls/ServiceColorPicker.vue";
import serviceServer from "../../server/service";
import { mapActions, mapState } from "vuex";
import {
  validateRequired,
  validateNumberNotNegative,
  validateNumberNotFloat
} from "../../helpers/validation";
import MultipleSelect from "../controls/MultipleSelect";
import ServiceBillingCodeCostVariationConditionEditor from "./ServiceBillingCodeCostVariationConditionEditor.vue";

export default {
  name: "ServiceCrud",
  components: {
    Grid,
    MultipleSelect,
    ServiceColorPicker,
    ServiceBillingCodeCostVariationConditionEditor
  },
  data() {
    return {
      list: [],
      serviceId: null,
      form: {
        color: "",
        name: null,
        billingCode: null,
        mod1: null,
        mod2: null,
        mod3: null,
        mod4: null,
        fullyPaidByPatient: false,
        formIds: [],
        defaultDurationMinutes: null,
        blockOffMinutesBefore: null,
        blockOffMinutesAfter: null,
        appointmentRequestEnable: null,
        appointmentRequestNewPatientEnable: null,
        costPerUnit: null,
        billable: true
      },
      formErrors: {
        name: [],
        billingCode: [],
        costPerUnit: [],
        modifiers: [],
        defaultDurationMinutes: [],
        blockOffMinutes: []
      },
      search: ""
    };
  },
  async mounted() {
    this.getServices();
    this.fetchFormBuilderForms({ category: "main" });
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("formBuilder", ["fetchFormBuilderForms"]),
    async getServices() {
      this.list = await serviceServer.query({
        type: "Service"
      });
    },
    addService() {
      this.clearErrors();
      this.serviceId = null;
      this.form = {
        color: "#ffffff",
        name: null,
        billingCode: null,
        costPerUnit: 0,
        mod1: null,
        mod2: null,
        mod3: null,
        mod4: null,
        fullyPaidByPatient: false,
        appointmentRequestEnable: false,
        appointmentRequestNewPatientEnable: false,
        defaultDurationMinutes: 30,
        blockOffMinutesBefore: 0,
        blockOffMinutesAfter: 0,
        formIds: [],
        type: "Service",
        billable: true
      };

      this.$refs["form-modal"].show();
    },
    async editService(id) {
      this.clearErrors();
      const service = await serviceServer.get(id);
      this.form = {
        color: service.color || "white",
        name: service.name,
        appointmentRequestEnable: service.appointmentRequestEnable,
        appointmentRequestNewPatientEnable:
          service.appointmentRequestNewPatientEnable,
        defaultDurationMinutes: service.defaultDurationMinutes,
        blockOffMinutesBefore: service.blockOffMinutesBefore,
        blockOffMinutesAfter: service.blockOffMinutesAfter,
        billingCode:
          service.billingCodes.length > 0
            ? service.billingCodes[0].cptCode
            : null,
        mod1:
          service.billingCodes.length > 0 ? service.billingCodes[0].mod1 : null,
        mod2:
          service.billingCodes.length > 0 ? service.billingCodes[0].mod2 : null,
        mod3:
          service.billingCodes.length > 0 ? service.billingCodes[0].mod3 : null,
        mod4:
          service.billingCodes.length > 0 ? service.billingCodes[0].mod4 : null,
        fullyPaidByPatient: service.fullyPaidByPatient,
        billingCodeId:
          service.billingCodes.length > 0 ? service.billingCodes[0].id : null,
        formIds: service.formIds.map(f => ({
          value: f.id,
          label: f.name,
          data: f
        })),
        costPerUnit:
          service.billingCodes.length > 0
            ? service.billingCodes[0].costPerUnit
            : 0,
        type: "Service",
        billable: service.billable,
        costVariations:
          service.billingCodes.length > 0
            ? service.billingCodes[0].serviceBillingCodeCostVariation
            : []
      };

      this.serviceId = id;
      this.$refs["form-modal"].show();
    },
    async deleteService(id) {
      this.$dialog
        .alert(
          {
            title: "Are you sure you want to delete this record?"
          },
          {
            view: "confirm-delete"
          }
        )
        .then(async () => {
          await serviceServer.delete(id);
          this.getServices();
          this.showAlertSuccess();
          this.dispatchListener({
            listener: "fetchDocumentMenus",
            param: "all"
          });
        });
    },
    async submitService() {
      if (!this.validateSubmit()) {
        return;
      }

      const currentVariations = this.$refs.conditionEditor.conditions.map(
        condition => ({
          costVariation: condition.costVariation,
          statements: condition.statements.map(statement => ({
            source: statement.field,
            value: statement.value,
            compareOperator: statement.logicalOperator
          })),
          billingCode: condition.billingCode,
          mod1: condition.mod1,
          mod2: condition.mod2,
          mod3: condition.mod3,
          mod4: condition.mod4
        })
      );

      const formData = {
        ...this.form,
        color: this.form.color == "#ffffff" ? null : this.form.color,
        name: this.form.name,
        billingCodes: [
          {
            id: this.form.billingCodeId,
            cptCode: this.form.billingCode,
            costPerUnit: this.form.costPerUnit
              ? parseFloat(this.form.costPerUnit)
              : 0,
            mod1: this.form.mod1,
            mod2: this.form.mod2,
            mod3: this.form.mod3,
            mod4: this.form.mod4,
            fullyPaidByPatient: this.form.fullyPaidByPatient,
            serviceBillingCodeCostVariations: currentVariations.map(cv => ({
              costPerUnit: cv.costVariation,
              costVariationRules: JSON.stringify(cv.statements),
              billingCode: cv.billingCode,
              mod1: cv.mod1,
              mod2: cv.mod2,
              mod3: cv.mod3,
              mod4: cv.mod4
            }))
          }
        ],
        type: "Service",
        costPerUnit: this.form.costPerUnit
          ? parseFloat(this.form.costPerUnit)
          : 0,
        appointmentRequestEnable: this.form.appointmentRequestEnable
          ? this.form.appointmentRequestEnable
          : false,
        appointmentRequestNewPatientEnable: this.form
          .appointmentRequestNewPatientEnable
          ? this.form.appointmentRequestNewPatientEnable
          : false,
        defaultDurationMinutes: this.form.defaultDurationMinutes
          ? parseInt(this.form.defaultDurationMinutes)
          : null,
        blockOffMinutesBefore: this.form.blockOffMinutesBefore
          ? parseInt(this.form.blockOffMinutesBefore)
          : null,
        blockOffMinutesAfter: this.form.blockOffMinutesAfter
          ? parseInt(this.form.blockOffMinutesAfter)
          : null,
        formIds: this.form.formIds.map(f => f.value)
      };

      if (this.serviceId) {
        await serviceServer.update(this.serviceId, formData);
      } else {
        await serviceServer.create(formData);
      }

      this.$refs["form-modal"].hide();
      this.getServices();
      this.showAlertSuccess();
    },
    validateSubmit() {
      let result = true;

      this.formErrors.name = [];
      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        result = false;
      }

      this.formErrors.billingCode = [];
      if (!validateRequired(this.form.billingCode)) {
        this.formErrors.billingCode.push("This field is required");
        result = false;
      } else if (this.form.billingCode.length > 50) {
        this.formErrors.billingCode.push(
          "The length must be 50 characters or fewer"
        );
        result = false;
      }

      this.formErrors.costPerUnit = [];
      if (!validateNumberNotNegative(this.form.costPerUnit)) {
        this.formErrors.costPerUnit.push(
          "This field must have a positive value"
        );
        result = false;
      }

      this.formErrors.defaultDurationMinutes = [];
      if (
        this.form.defaultDurationMinutes &&
        !validateNumberNotNegative(this.form.defaultDurationMinutes)
      ) {
        this.formErrors.defaultDurationMinutes.push(
          "This field must have a positive value"
        );
        result = false;
      }
      if (
        this.form.defaultDurationMinutes != null &&
        !validateNumberNotFloat(this.form.defaultDurationMinutes)
      ) {
        this.formErrors.defaultDurationMinutes.push(
          "This field must have an integer value"
        );
        result = false;
      }

      this.formErrors.blockOffMinutes = [];
      if (
        (this.form.blockOffMinutesBefore &&
          !validateNumberNotNegative(this.form.blockOffMinutesBefore)) ||
        (this.form.blockOffMinutesAfter &&
          !validateNumberNotNegative(this.form.blockOffMinutesAfter))
      ) {
        this.formErrors.blockOffMinutes.push(
          "This fields must have a positive value"
        );
        result = false;
      }
      if (
        (this.form.blockOffMinutesBefore != null &&
          !validateNumberNotFloat(this.form.blockOffMinutesBefore)) ||
        (this.form.blockOffMinutesAfter != null &&
          !validateNumberNotFloat(this.form.blockOffMinutesAfter))
      ) {
        this.formErrors.blockOffMinutes.push(
          "This fields must have an integer value"
        );
        result = false;
      }

      this.formErrors.modifiers = [];
      if (this.form.mod1 && this.form.mod1.length > 20) {
        this.formErrors.modifiers.push(
          "The length of Modifier 1 must be 20 characters or fewer"
        );
        result = false;
      }

      if (this.form.mod2 && this.form.mod2.length > 20) {
        this.formErrors.modifiers.push(
          "The length of Modifier 2 must be 20 characters or fewer"
        );
        result = false;
      }

      if (this.form.mod3 && this.form.mod3.length > 20) {
        this.formErrors.modifiers.push(
          "The length of Modifier 3 must be 20 characters or fewer"
        );
        result = false;
      }

      if (this.form.mod4 && this.form.mod4.length > 20) {
        this.formErrors.modifiers.push(
          "The length of Modifier 4 must be 20 characters or fewer"
        );
        result = false;
      }

      return result;
    },
    clearErrors() {
      this.formErrors.name = [];
      this.formErrors.billingCode = [];
      this.formErrors.costPerUnit = [];
      this.formErrors.modifiers = [];
      this.formErrors.defaultDurationMinutes = [];
      this.formErrors.blockOffMinutes = [];
    },
    isLetterOrNumber(e) {
      let char = String.fromCharCode(e.keyCode);
      if (/[a-zA-Z0-9-]/.test(char)) return true;
      else e.preventDefault();
    },
    async removeColor() {
      if (this.form.color != "#ffffff") {
        this.form.color = "#ffffff";
      }
    }
  },
  computed: {
    ...mapState("formBuilder", {
      formOptions: state =>
        [...state.list["main"], ...state.list["masterTreatmentPlan"]]
          .map(f => ({
            value: f.id,
            label: f.name,
            data: f
          }))
          .sort((a, b) =>
            a.label.toLowerCase().localeCompare(b.label.toLowerCase())
          )
    }),
    items() {
      let items = this.list.slice();

      if (this.search) {
        const caseInsensitiveIncludes = (text, char) =>
          text?.toUpperCase().includes(char?.toUpperCase());
        items = items.filter(
          item =>
            caseInsensitiveIncludes(item.name, this.search) ||
            caseInsensitiveIncludes(
              item.billingCodes[0]?.cptCode,
              this.search
            ) ||
            caseInsensitiveIncludes(item.billingCodes[0]?.mod1, this.search) ||
            caseInsensitiveIncludes(item.billingCodes[0]?.mod2, this.search) ||
            caseInsensitiveIncludes(item.billingCodes[0]?.mod3, this.search) ||
            caseInsensitiveIncludes(item.billingCodes[0]?.mod4, this.search) ||
            caseInsensitiveIncludes(item.formIds[0]?.name, this.search)
        );
      }

      return items.map(item => ({
        id: item.id,
        name: item.name || "",
        color: item.color || null,
        billingCode:
          item.billingCodes.length > 0 ? item.billingCodes[0].cptCode : "",
        mod1: item.billingCodes.length > 0 ? item.billingCodes[0].mod1 : "",
        mod2: item.billingCodes.length > 0 ? item.billingCodes[0].mod2 : "",
        mod3: item.billingCodes.length > 0 ? item.billingCodes[0].mod3 : "",
        mod4: item.billingCodes.length > 0 ? item.billingCodes[0].mod4 : "",
        appointmentRequestEnable: item.appointmentRequestEnable || "",
        appointmentRequestNewPatientEnable:
          item.appointmentRequestNewPatientEnable || "",
        defaultDurationMinutes: item.defaultDurationMinutes || "",
        blockOffMinutesBefore: item.blockOffMinutesBefore || "",
        blockOffMinutesAfter: item.blockOffMinutesAfter || "",
        formIds: item.formIds || [],
        costPerUnit:
          item.billingCodes.length > 0 ? item.billingCodes[0].costPerUnit : ""
      }));
    },
    columns() {
      let columns = [
        {
          text: "Name",
          field: "name",
          flex: 1,
          sortable(a, b) {
            return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
          }
        },
        {
          text: "Color",
          field: "color",
          type: "template",
          width: 70,
          align: "center",
          template: data => {
            return `<div class="d-flex w-100 justify-content-center"><div class="small-square" style="background-color:${
              data.record.data.color ? data.record.data.color : null
            };"></div></div>`;
          },
          sortable: false
        },
        {
          text: "Billing code",
          field: "billingCode",
          flex: 1,
          sortable(a, b) {
            return a.billingCode
              .toLowerCase()
              .localeCompare(b.billingCode.toLowerCase());
          }
        },
        {
          text: "Modifier 1",
          field: "mod1",
          flex: 1,
          sortable(a, b) {
            return a.mod1.toLowerCase().localeCompare(b.mod1.toLowerCase());
          }
        },
        {
          text: "Modifier 2",
          field: "mod2",
          flex: 1,
          sortable(a, b) {
            return a.mod2.toLowerCase().localeCompare(b.mod2.toLowerCase());
          }
        },
        {
          text: "Modifier 3",
          field: "mod3",
          flex: 1,
          sortable(a, b) {
            return a.mod3.toLowerCase().localeCompare(b.mod3.toLowerCase());
          }
        },
        {
          text: "Modifier 4",
          field: "mod4",
          flex: 1,
          sortable(a, b) {
            return a.mod4.toLowerCase().localeCompare(b.mod4.toLowerCase());
          }
        },
        {
          text: "Cost Per Unit ($)",
          field: "costPerUnit",
          flex: 1,
          sortable(a, b) {
            return a.costPerUnit
              .toLowerCase()
              .localeCompare(b.costPerUnit.toLowerCase());
          }
        },
        {
          text: "Linked documents",
          field: "linkedDocuments",
          type: "template",
          flex: 1,
          template: data =>
            data.record.data.formIds.map(f => f.name).join(", "),
          sortable: false
        },
        {
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          align: "right",
          width: 80,
          actions: [
            {
              tooltip: "Edit",
              renderer: () =>
                `<img class="b-action-item" src="${
                  this.isStandalone()
                    ? `/icons/edit.svg`
                    : `/desktop/icons/edit.svg`
                }">`,
              onClick: ({ record }) => {
                this.editService(record.data.id);
              }
            },
            {
              tooltip: "Delete",
              renderer: () =>
                `<img class="b-action-item ml-2" src="${
                  this.isStandalone()
                    ? `/icons/delete.svg`
                    : `/desktop/icons/delete.svg`
                }">`,
              onClick: ({ record }) => {
                this.deleteService(record.data.id);
              }
            }
          ]
        }
      ];
      return columns;
    }
  }
};
</script>

<style scoped>
.settings-services-container >>> .b-grid-container .b-container {
  max-height: calc(100vh - 381px);
}

.form-item:hover {
  cursor: pointer;
}

.forms-list {
  max-height: 200px;
  overflow-y: auto;
}

div >>> .custom-container > .modal-dialog {
  max-width: 1024px;
}

.custom-label {
  padding-left: 0;
  margin-left: 0;
  margin-top: 35px;
}

.r-border::after {
  content: "";
  position: absolute;
  top: 1%;
  right: 0;
  width: 2px;
  height: 100%;
  background-color: #f3f5f6;
}
.collapsed > .when-opened,
:not(.collapsed) > .when-closed {
  display: none;
}

.modal-body {
  max-height: 500px;
  overflow: auto;
}
</style>
